import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { logoBase64 } from '../../../../assets/logoBase64';

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: typeof autoTable;
    lastAutoTable: {
      finalY: number;
    };
  }
}

interface DetalleLinea {
  presentacion: string;
  calibre: string;
  kilos: number;
  precioMP: number;
  montoMP: number;
  precioServicio: number;
  montoServicio: number;
}

interface OrdenLiquidacionPdfData {
  numero: string;
  fecha: string;
  numeroDocumento: string;
  proveedor: string;
  asociacion: string;
  duenio: string;
  encargado: string;
  mallas: number;
  subLoteCantidad: number;
  tipoProceso: string;
  tipoOperacion: string;
  tipoOperacionLabel: string;
  detalles: DetalleLinea[];
  totalKilos: number;
  subTotal: number;
  detraccion: number;
  totalServicio: number;
  totalLiquidar: number;
  servicioAplica: boolean;
  observaciones?: string;
}

const formatCurrency = (n: number) =>
  `S/ ${n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatNumber = (n: number, decimals = 2) =>
  n.toLocaleString('es-PE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateForFileName = (dateStr: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
};

export const ordenLiquidacionPdfService = {
  generatePdf(data: OrdenLiquidacionPdfData): void {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = margin;

    // ==========================================
    // HEADER
    // ==========================================
    const logoWidth = 25;
    const logoHeight = 17;
    doc.addImage(logoBase64, 'PNG', margin, yPos, logoWidth, logoHeight);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('ORDEN DE LIQUIDACION', pageWidth / 2, yPos + 8, { align: 'center' });

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('CPP. ISABEL GOMEZ DE VIZA', pageWidth - margin, yPos + 5, { align: 'right' });

    // Numero de orden
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`N° ${data.numero}`, pageWidth - margin, yPos + 13, { align: 'right' });

    yPos += 24;

    // ==========================================
    // INFORMACION GENERAL
    // ==========================================
    doc.setFontSize(8);
    const col1 = margin + 3;
    const col2 = pageWidth / 2 + 10;
    const labelWidth = 30;
    const lineHeight = 5;

    const drawField = (label: string, value: string, x: number, y: number) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}:`, x, y);
      doc.setFont('helvetica', 'normal');
      doc.text(value, x + labelWidth, y);
    };

    // Borde del bloque info
    const infoStartY = yPos - 2;
    drawField('N° DERC', data.numeroDocumento || '-', col1, yPos);
    drawField('Fecha', formatDate(data.fecha), col2, yPos);
    yPos += lineHeight;

    drawField('Proveedor', data.proveedor || '-', col1, yPos);
    drawField('Asociacion', data.asociacion || '-', col2, yPos);
    yPos += lineHeight;

    drawField('Duenio', data.duenio || '-', col1, yPos);
    drawField('Encargado', data.encargado || '-', col2, yPos);
    yPos += lineHeight;

    drawField('Mallas', String(data.mallas || 0), col1, yPos);
    drawField('Sub Lote Cant.', String(data.subLoteCantidad || 0), col2, yPos);
    yPos += lineHeight;

    drawField('Proceso', data.tipoProceso, col1, yPos);
    drawField('Operacion', data.tipoOperacionLabel, col2, yPos);

    const infoEndY = yPos + 5;
    doc.setDrawColor(180);
    doc.rect(margin, infoStartY - 1, pageWidth - 2 * margin, infoEndY - infoStartY + 1);

    yPos = infoEndY + 5;

    // ==========================================
    // TABLA DE DETALLES
    // ==========================================

    // Group by presentacion
    const presentaciones = [...new Set(data.detalles.map((d) => d.presentacion))];

    const tableHeaders = data.servicioAplica
      ? [['Presentacion', 'Calibre', 'Kilos', 'Precio MP', 'Monto MP', 'Precio Serv.', 'Monto Serv.']]
      : [['Presentacion', 'Calibre', 'Kilos', 'Precio MP', 'Monto MP']];

    const tableBody: (string | { content: string; styles?: object })[][] = [];

    for (const pres of presentaciones) {
      const items = data.detalles.filter((d) => d.presentacion === pres);
      const subKilos = items.reduce((s, d) => s + d.kilos, 0);
      const subMontoMP = items.reduce((s, d) => s + d.montoMP, 0);
      const subMontoServ = items.reduce((s, d) => s + d.montoServicio, 0);

      for (let i = 0; i < items.length; i++) {
        const det = items[i];
        const row: (string | { content: string; styles?: object })[] = [
          i === 0 ? { content: pres, styles: { fontStyle: 'bold' } } : '',
          det.calibre,
          formatNumber(det.kilos),
          formatCurrency(det.precioMP),
          formatCurrency(det.montoMP),
        ];
        if (data.servicioAplica) {
          row.push(formatCurrency(det.precioServicio));
          row.push(formatCurrency(det.montoServicio));
        }
        tableBody.push(row);
      }

      // Subtotal row
      const subRow: (string | { content: string; styles?: object })[] = [
        { content: `Subtotal ${pres}`, styles: { fontStyle: 'bold' } },
        '',
        { content: formatNumber(subKilos), styles: { fontStyle: 'bold' } },
        '',
        { content: formatCurrency(subMontoMP), styles: { fontStyle: 'bold' } },
      ];
      if (data.servicioAplica) {
        subRow.push('');
        subRow.push({ content: formatCurrency(subMontoServ), styles: { fontStyle: 'bold' } });
      }
      tableBody.push(subRow);
    }

    autoTable(doc, {
      startY: yPos,
      head: tableHeaders,
      body: tableBody,
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 7.5,
        cellPadding: 1.5,
      },
      headStyles: {
        fillColor: [41, 65, 90],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 22, halign: 'center' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right' },
      },
      theme: 'grid',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      didParseCell: (hookData: any) => {
        // Style subtotal rows
        if (hookData.section === 'body') {
          const firstCell = tableBody[hookData.row.index]?.[0];
          if (typeof firstCell === 'object' && firstCell?.content?.startsWith('Subtotal')) {
            hookData.cell.styles.fillColor = [240, 240, 240];
          }
        }
      },
    });

    yPos = doc.lastAutoTable.finalY + 8;

    // ==========================================
    // RESUMEN DE LIQUIDACION
    // ==========================================
    const summaryX = pageWidth - margin - 85;
    const summaryLabelX = summaryX;
    const summaryValueX = pageWidth - margin;
    const summaryWidth = 87;

    // Title
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('RESUMEN DE LIQUIDACION', summaryX, yPos);
    yPos += 2;

    doc.setDrawColor(41, 65, 90);
    doc.setLineWidth(0.5);
    doc.line(summaryX, yPos, summaryX + summaryWidth, yPos);
    yPos += 5;

    doc.setFontSize(8);

    // Total Kilos
    doc.setFont('helvetica', 'normal');
    doc.text('Total Kilos:', summaryLabelX, yPos);
    doc.text(formatNumber(data.totalKilos), summaryValueX, yPos, { align: 'right' });
    yPos += lineHeight;

    // SubTotal
    doc.setFont('helvetica', 'normal');
    doc.text('SubTotal:', summaryLabelX, yPos);
    doc.text(formatCurrency(data.subTotal), summaryValueX, yPos, { align: 'right' });
    yPos += lineHeight;

    // Detraccion
    doc.setFont('helvetica', 'normal');
    doc.text('Detraccion (1.5%):', summaryLabelX, yPos);
    doc.setTextColor(200, 0, 0);
    doc.text(`- ${formatCurrency(data.detraccion)}`, summaryValueX, yPos, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    yPos += lineHeight;

    // Servicio
    if (data.servicioAplica) {
      doc.setFont('helvetica', 'normal');
      doc.text('Servicio:', summaryLabelX, yPos);
      doc.setTextColor(200, 0, 0);
      doc.text(`- ${formatCurrency(data.totalServicio)}`, summaryValueX, yPos, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      yPos += lineHeight;
    }

    // Divider
    yPos += 1;
    doc.setDrawColor(41, 65, 90);
    doc.setLineWidth(0.5);
    doc.line(summaryX, yPos, summaryX + summaryWidth, yPos);
    yPos += 5;

    // Total a Liquidar
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL A LIQUIDAR:', summaryLabelX, yPos);
    doc.setTextColor(0, 80, 160);
    doc.text(formatCurrency(data.totalLiquidar), summaryValueX, yPos, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    yPos += 10;

    // ==========================================
    // OBSERVACIONES
    // ==========================================
    if (data.observaciones && data.observaciones.trim().length > 0) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('OBSERVACIONES:', margin, yPos);
      yPos += 4;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      const obsLines = doc.splitTextToSize(data.observaciones, pageWidth - 2 * margin);
      doc.text(obsLines, margin, yPos);
      yPos += obsLines.length * 3.5 + 3;
    }

    yPos += 5;

    // ==========================================
    // FIRMAS
    // ==========================================
    const firmaWidth = 60;
    const firmaY = yPos + 15;
    const firma1X = margin + 20;
    const firma2X = pageWidth - margin - firmaWidth - 20;

    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');

    // Firma 1
    doc.line(firma1X, firmaY, firma1X + firmaWidth, firmaY);
    doc.text('JEFE DE PRODUCCION', firma1X + firmaWidth / 2, firmaY + 4, { align: 'center' });

    // Firma 2
    doc.line(firma2X, firmaY, firma2X + firmaWidth, firmaY);
    doc.text('PROVEEDOR', firma2X + firmaWidth / 2, firmaY + 4, { align: 'center' });

    // ==========================================
    // SAVE
    // ==========================================
    const fileName = `Orden_Liquidacion_${data.numero}_${formatDateForFileName(data.fecha)}.pdf`;
    doc.save(fileName);
  },
};
